import React, { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const cn = (...classes) => classes.filter(Boolean).join(" ");

export default function HoverPlayCard({
  src,
  poster,
  className,
  loop = false,
  mutedOnHover = true,
}) {
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [userStarted, setUserStarted] = useState(false); // true when user clicked play
  const [prevMuted, setPrevMuted] = useState(null);

  // Touch devices have no real hover: on iOS in particular, attaching
  // mouseenter/mouseleave to a tappable element makes the browser eat the
  // first tap as a "ghost hover" (to support legacy :hover-reveal UIs),
  // silently starting the muted preview before the real click ever fires.
  // That's exactly why audio only kicked in on the second tap. Skipping
  // the hover-preview wiring entirely on touch avoids the ghost-hover tap
  // and makes the very first tap go straight to play-with-sound.
  const [supportsHover] = useState(() => {
    try {
      return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    } catch {
      return true;
    }
  });

  // Play muted on hover (if allowed). Pause on leave. Skipped entirely on
  // touch devices — see supportsHover above.
  useEffect(() => {
    if (!supportsHover) return;
    const video = videoRef.current;
    if (!video) return;

    let ignore = false;

    const doPlayMuted = async () => {
      if (!video) return;
      if (prevMuted === null) setPrevMuted(video.muted);

      if (mutedOnHover) {
        video.muted = true;
        setIsMuted(true);
      }
      try {
        await video.play();
        if (!ignore) setIsPlaying(true);
      } catch {
        if (!ignore) setIsPlaying(false);
      }
    };

    const doPause = () => {
      if (!video) return;
      video.pause();
      setIsPlaying(false);
      if (!userStarted && prevMuted !== null) {
        video.muted = prevMuted;
        setIsMuted(prevMuted);
      }
    };

    if (isHovering && !userStarted) {
      void doPlayMuted();
    } else if (!isHovering && !userStarted) {
      doPause();
    }

    return () => {
      ignore = true;
    };
  }, [isHovering, mutedOnHover, userStarted]);

  // If user clicks the icon: toggle playback and treat as a user gesture.
  // Keyed off the video's real muted state, not isPlaying — hovering already
  // sets isPlaying=true (muted preview), so checking isPlaying here would
  // make this click pause the silent preview instead of unmuting it.
  const handleIconClick = async () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.muted || video.paused) {
      setUserStarted(true);
      video.muted = false;
      setIsMuted(false);
      setPrevMuted(false);
      try {
        await video.play();
        setIsPlaying(true);
      } catch (err) {
        console.error("Play failed:", err);
        setIsPlaying(false);
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  // keep visual play state in sync with native events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setUserStarted(false);
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
    };
  }, []);

  // "Playing" from the viewer's perspective means audible playback, not just
  // a running <video> element — the muted hover preview is technically
  // playing too, but should still show the play icon, not pause.
  const audiblyPlaying = isPlaying && !isMuted;

  return (
    <div
      className={cn(
        "relative overflow-hidden group",
        className
      )}
      onMouseEnter={supportsHover ? () => setIsHovering(true) : undefined}
      onMouseLeave={supportsHover ? () => setIsHovering(false) : undefined}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        loop={loop}
        playsInline
        className="w-full h-full object-cover"
      />

      <AnimatePresence>
        {(isHovering || !isPlaying || !supportsHover) && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleIconClick();
              }}
              className="pointer-events-auto bg-black/20 hover:bg-black/40 text-white rounded-full w-16 h-16 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              aria-label={audiblyPlaying ? "Pause video" : "Play video"}
            >
              {audiblyPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute left-3 bottom-3 text-xs text-white/80 bg-black/20 px-2 py-1 rounded-full">
        {audiblyPlaying ? "Reproduciendo" : "Pausado"}
      </div>
    </div>
  );
}
